/**
 * MODULE: _shared/frontend-shell/src/components/ErrorBoundary.tsx
 * PURPOSE: Catch and display React errors gracefully
 * DEPENDENCIES: react, ./lib/reloadOnStaleChunk
 * PUBLIC_API: ErrorBoundary
 * SIDE_EFFECTS: Error logging; one-shot reload on stale deploy chunks [2.2.2128]
 * AGENT SAFETY: 🟢 SAFE
 * RECENT: 2026-08-06 [2.2.2130] — ACA sprint close-out (docs + Playwright same-origin contracts)
 * RECENT_PRIOR: 2026-08-05 [2.2.2128] — auto-reload once on dynamic import / chunk load failures
 * RECENT_PRIOR: 2026-03-30 [2.2.214] — process.env.NODE_ENV via @types/node
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { isStaleChunkMessage } from '../lib/reloadOnStaleChunk';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Post-deploy: old tab holds stale hashed imports — hard reload once for fresh index.html
    if (isStaleChunkMessage(error.message)) {
      try {
        if (sessionStorage.getItem('brazn:stale-chunk-reload') !== '1') {
          sessionStorage.setItem('brazn:stale-chunk-reload', '1');
          window.location.reload();
          return;
        }
      } catch {
        window.location.reload();
        return;
      }
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Something went wrong</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              We're sorry, but something unexpected happened. Please try
              refreshing the page.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-8 text-left bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                <summary className="cursor-pointer font-semibold text-gray-900 dark:text-white mb-2">Error details</summary>
                <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto mt-2 whitespace-pre-wrap">{this.state.error.toString()}</pre>
                <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto mt-2 whitespace-pre-wrap">{this.state.errorInfo?.componentStack}</pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={this.handleReset} 
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
